import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Table,
    Sheet,
    Divider
} from '@mui/joy';
import QuizIcon from '@mui/icons-material/Quiz';
import AssignmentIcon from '@mui/icons-material/Assignment';
import axiosConfig from '../util/axiosConfig';

const TestSchedule = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [testData, setTestData] = useState({
        scheduledTests: [],
        examPermissions: []
    });

    useEffect(() => {
        fetchTestSchedule();
    }, []);

    const fetchTestSchedule = async () => {
        try {
            setError(null);
            const response = await axiosConfig.get('/students/test-schedule');
            setTestData(response.data);
        } catch (err) {
            console.error('Error fetching test schedule:', err);
            setError(err.response?.data?.errorMessage || 'Failed to load test schedule data');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } catch (e) {
            return dateString;
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading test schedule...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            </Box>
        );
    }

    const hasScheduledTests = testData.scheduledTests && testData.scheduledTests.length > 0;
    const hasExamPermissions = testData.examPermissions && testData.examPermissions.length > 0;
    const hasAnyData = hasScheduledTests || hasExamPermissions;

    return (
        <Box sx={{ p: 2, maxWidth: '1400px', mx: 'auto' }}>


            {!hasAnyData ? (
                <Card variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                    <Typography level="h4" sx={{ fontWeight: 'bold', color: '#6e7072' }}>
                        No test has been scheduled
                    </Typography>
                </Card>
            ) : (
                <>
                    {hasExamPermissions && (
                        <>
                            <Card variant="outlined" sx={{ mb: 3 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <AssignmentIcon sx={{ color: '#eb7a24', fontSize: '1.5rem' }} />
                                        <Typography level="h3" sx={{ color: '#eb7a24', fontWeight: 'bold' }}>
                                            Exam Permissions
                                        </Typography>
                                    </Box>
                                    <Sheet
                                        variant="outlined"
                                        sx={{
                                            width: '100%',
                                            borderRadius: 'sm',
                                            overflow: 'auto',
                                            maxHeight: '600px',
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        <Table
                                            aria-label="exam permissions table"
                                            sx={{
                                                '& tbody tr:nth-child(even)': {
                                                    backgroundColor: '#f9f9f9'
                                                },
                                                '& tbody tr:hover': {
                                                    backgroundColor: '#fff5ed'
                                                },
                                                '& thead': {
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: 10
                                                },
                                                '& thead th': {
                                                    backgroundColor: '#eb7a24',
                                                    color: 'white',
                                                    fontWeight: 'bold',
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: 10,
                                                    borderBottom: '2px solid',
                                                    borderBottomColor: '#e55125'
                                                },
                                                '& td, & th': {
                                                    padding: '12px',
                                                    border: '1px solid',
                                                    borderColor: 'divider'
                                                }
                                            }}
                                        >
                                            <thead>
                                                <tr>
                                                    <th>Test</th>
                                                    <th>Date</th>
                                                    <th>Marks</th>
                                                    <th>Duration</th>
                                                    <th>Permission with</th>
                                                    <th>Permission for Exam</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {testData.examPermissions.map((permission, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <Typography level="body-md">
                                                                {permission.testName || permission.mtestid || '-'}
                                                            </Typography>
                                                        </td>
                                                        <td>
                                                            <Typography level="body-md">
                                                                {formatDate(permission.testDate)}
                                                            </Typography>
                                                        </td>
                                                        <td>
                                                            <Typography level="body-md">
                                                                {permission.testMarks || '-'}
                                                            </Typography>
                                                        </td>
                                                        <td>
                                                            <Typography level="body-md">
                                                                {permission.testDuration || '-'}
                                                            </Typography>
                                                        </td>
                                                        <td>
                                                            <Typography level="body-md">
                                                                {permission.batchCode || '-'}
                                                            </Typography>
                                                        </td>
                                                        <td>
                                                            <Typography level="body-md">
                                                                {permission.testId || '-'}
                                                            </Typography>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </Sheet>
                                </CardContent>
                            </Card>
                            <Divider sx={{ my: 3 }} />
                        </>
                    )}

                    {hasScheduledTests && (
                        <Card variant="outlined">
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <QuizIcon sx={{ color: '#eb7a24', fontSize: '1.5rem' }} />
                                    <Typography level="h3" sx={{ color: '#eb7a24', fontWeight: 'bold' }}>
                                        Scheduled Tests
                                    </Typography>
                                </Box>
                                <Sheet
                                    variant="outlined"
                                    sx={{
                                        width: '100%',
                                        borderRadius: 'sm',
                                        overflow: 'auto',
                                        maxHeight: '600px',
                                        border: '1px solid',
                                        borderColor: 'divider'
                                    }}
                                >
                                    <Table
                                        aria-label="scheduled tests table"
                                        sx={{
                                            '& tbody tr:nth-child(even)': {
                                                backgroundColor: '#f9f9f9'
                                            },
                                            '& tbody tr:hover': {
                                                backgroundColor: '#fff5ed'
                                            },
                                            '& thead': {
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 10
                                            },
                                            '& thead th': {
                                                backgroundColor: '#eb7a24',
                                                color: 'white',
                                                fontWeight: 'bold',
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 10,
                                                borderBottom: '2px solid',
                                                borderBottomColor: '#e55125'
                                            },
                                            '& td, & th': {
                                                padding: '12px',
                                                border: '1px solid',
                                                borderColor: 'divider'
                                            }
                                        }}
                                    >
                                        <thead>
                                            <tr>
                                                <th>Test</th>
                                                <th>Date</th>
                                                <th>Marks</th>
                                                <th>Duration</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {testData.scheduledTests.map((test, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <Typography level="body-md">
                                                            {test.testName || '-'}
                                                        </Typography>
                                                    </td>
                                                    <td>
                                                        <Typography level="body-md">
                                                            {formatDate(test.testDate)}
                                                        </Typography>
                                                    </td>
                                                    <td>
                                                        <Typography level="body-md">
                                                            {test.testMarks || '-'}
                                                        </Typography>
                                                    </td>
                                                    <td>
                                                        <Typography level="body-md">
                                                            {test.testDuration || '-'}
                                                        </Typography>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Sheet>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}
        </Box>
    );
};

export default TestSchedule;

